import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { environment } from '../../../../environments/environment';
import { first, map } from 'rxjs/operators';
import {
  CategoriesGroup,
  Category,
  Currencies,
  Event,
  EventCreateOptions,
  EventStatus,
  Organizer,
  OrganizerAccessRight,
  Platforms,
  TicketingPlatform,
  IWeezeventEvent,
  defaultCategoriesGroup,
  defaultCategory,
  IWeezeventGroup,
  currencies,
  IWeezeventRate,
  Language,
  AutomationsStatus,
  PayoutPolicy,
} from '../../../models';
import { PartnerUtils } from './utils';

@Injectable({
  providedIn: 'root',
})
export class WeezeventEventService {
  private http = inject(HttpClient);

  configUrl = environment.apiUrl;


  getWeezEvents(organizerId: number, removeExpired: boolean) {
    let route = `${this.configUrl}w/a/weezEvents/organizations/${organizerId}/events`;
    if (removeExpired) {
      route = route + '?removeExpired=true';
    }
    return this.http.get<{
      organizer: Organizer,
      reelaxEvents: Event[],
      weezEvents: IWeezeventEvent[],
    }>(route).pipe(
      first(),
    );
  }

  loadEvents(options: EventCreateOptions) {
    return this.getWeezEvents(
      options.organizer.id,
      options.removeExpired,
    ).pipe(
      map( (events) => events?.weezEvents?.map((e) => this.mapWeezToReelaxEvent(
        e,
        events.reelaxEvents,
        events.organizer,
        options.eventTemplate,
        options.priceScale,
        options.defaultStatus,
      ))),
    );
  }

  buildDescription(rate: IWeezeventRate) {
    if (!rate.description) {
      return undefined;
    }
    const description = {};
    description[Language.frFR] = rate.description;
    if (rate.translations?.description?.['fr-fr']) {
      description[Language.frFR] = rate.translations?.description?.['fr-fr'];
    } else {
      description[Language.frFR] = rate.description;
    }
    if (rate.translations?.description?.['en-gb']) {
      description[Language.enUS] = rate.translations?.description?.['en-gb'];
    }
    if (rate.translations?.description?.['es-es']) {
      description[Language.esES] = rate.translations?.description?.['es-es'];
    }
    return description;
  }

  mapWeezToReelaxEvent(
    event: IWeezeventEvent,
    reelaxEvents: Event[],
    organizer: Organizer,
    eventTemplate: Event,
    priceScale: number,
    defaultStatus: EventStatus,
  ) {
    const newEventDateStart = new Date(event.start_date);
    const endDate = (startDate: Date, endDate: Date) => {
      if (endDate && (
        new Date(startDate).getDate() !== new Date(endDate).getDate() ||
        new Date(startDate).getMonth() !== new Date(endDate).getMonth() ||
        new Date(startDate).getFullYear() !== new Date(endDate).getFullYear()
      )) {
        return new Date(endDate);
      }
      return undefined;
    };
    const weezGroupData: {[id: number]: IWeezeventGroup} = {};
    event.rates.forEach( (r) => {
      if (r.group && !weezGroupData[r.group?.id]) {
        weezGroupData[r.group?.id] = r.group;
      }
    });
    const parseStringPrice = (strPrice: string) => parseInt(strPrice.replace('.', ''), 10);
    const parseWeezeventPrice = (rate: IWeezeventRate) =>
      parseStringPrice(rate.price) + rate.fees.reduce((tot, f) => tot + parseStringPrice(f.amount), 0);
    return {
      ...eventTemplate,
      id: reelaxEvents.find(
        (r) => r.TicketingPlatforms.some((t) => t.platformEventId === event.id.toString()),
      )?.id ?? undefined,
      name: event.name,
      organizerName: eventTemplate?.organizerName ?? organizer?.name,
      location: [event.address?.city, event.address?.country].filter((x) => !!x).join(','),
      dateStart: newEventDateStart,
      dateEnd: endDate(event.start_date, event.end_date),
      resaleEnd: PartnerUtils.computeDateRelativeTo(
        eventTemplate?.resaleEnd,
        newEventDateStart,
        eventTemplate?.dateStart,
      ),
      imgUrl: event.full_picture,
      address: [
        event.address?.address1,
        event.address?.address2,
        event.address?.city,
        event.address?.zip_code,
        event.address?.state,
        event.address?.country,
      ].filter((x) => !!x).join(', '),
      isPartner: true,
      allowPayinRefund: true,
      status: defaultStatus ? defaultStatus : EventStatus.Online,
      capacity: undefined,
      reelaxEvent:  reelaxEvents.find(
        (r) => r.TicketingPlatforms.some((t) => t.platformEventId === `${event.id}`),
      ),
      Categories: event.rates.map( (rate, index) => {
        const price = parseWeezeventPrice(rate);
        return new Category({
          ...defaultCategory,
          ...(eventTemplate?.Categories?.[0]),
          available: !rate.disabled && price > 0,
          capacity: rate.quantity,
          currency: currencies.find( (c) => c === rate.currency) ?? Currencies.Euro,
          description: rate.description ? this.buildDescription(rate) : undefined,
          expiryDate: rate.visibility_end_date && new Date(rate.visibility_end_date) > new Date(event.start_date) ?
            new Date(rate.visibility_end_date) : undefined,
          name: rate.distributor?.label ? rate.distributor.label + ' - ' + rate.name : rate.name,
          price,
          minimalSalePrice: priceScale ? Math.round(price * priceScale) :  Math.round(price * 0.60),
          maximalSalePrice: price,
          order: rate.display_order ?? index,
          platformsForeignIds: {weezevent: `${rate.id}`},
          categoriesGroupId: rate.group?.id,
        });
      }),
      CategoriesGroups: Object.values(weezGroupData).map( (group, i) => new CategoriesGroup({
        ...defaultCategoriesGroup,
        name: group.name,
        order: group.display_order,
        platformsForeignIds: {[Platforms.Weezevent]: group.id?.toString()},
      })),
      TicketingPlatforms: [new TicketingPlatform({
        platform: Platforms.Weezevent,
        platformEventId: event.id?.toString(),
        platformOrganizerId: organizer.platformsForeignIds[Platforms.Weezevent],
        payoutPolicy: PayoutPolicy.Auto,
      })],
      Organizers: eventTemplate?.Organizers?.length >= 1 ? [
        ...eventTemplate.Organizers,
        new Organizer({
          ...organizer,
          EventOrganizer: {
            accessRight: OrganizerAccessRight.Owner,
          },
        }),
      ] : [new Organizer({
        ...organizer,
        EventOrganizer: {
          accessRight: OrganizerAccessRight.Owner,
        },
      })],
      PostalAddress: {
        address1: event.address?.address1,
        address2: event.address?.address2,
        city: event.address?.city,
        zip_code: event.address?.zip_code,
        state: event.address?.state,
        country: event.address?.country,
      },
      Automations: eventTemplate?.Automations?.map( (automation) => {
        const oldAutoTime = new Date(automation.time);
        const templateDateStart = automation?.eventDateStart ?
          new Date(automation?.eventDateStart) : new Date(eventTemplate?.dateStart);
        const newTime = new Date(
          newEventDateStart.getTime() - (templateDateStart.getTime() - oldAutoTime.getTime()),
        ).toString();
        return {
          status: AutomationsStatus.Created,
          task: automation.task,
          time: newTime,
          type: automation.type,
        };
      }),
      WaitListConfig: eventTemplate?.WaitListConfig ?? undefined,
      EventPersonalization: eventTemplate?.EventPersonalization ?? undefined,
    };
  }

}
